import type { AppNotificationItem } from "@nlc/shared-types";
import type { CustomNotificationCard } from "@suprsend/react";

import { UserRoleEnum } from "@nlc/shared-types";
import { Inbox } from "@suprsend/react";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import {
  Bell,
  ChevronDown,
  ClipboardCheck,
  CreditCard,
  GripIcon,
  LogOut,
  PanelLeftIcon,
  Settings,
  User,
  User2Icon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { DialogTrigger } from "react-aria-components";

import { FullLogo } from "@/assets/FullLogo";
import { AccessibleIcon } from "@/components/accessible-icon";
import { Avatar } from "@/components/avatar";
import { Button } from "@/components/button";
import { Dialog } from "@/components/dialog";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItemLabel,
  MenuPopover,
  MenuSeparator,
  MenuTrigger,
} from "@/components/menu";
import { Popover } from "@/components/popover";
import { useSidebar } from "@/components/sidebar";
import { Switch } from "@/components/switch";
import { env } from "@/config/env";
import {
  useAppNotifications,
  useBulkUpdateAppNotifications,
  useUnreadNotificationCounts,
  useUpdateAppNotification,
} from "@/hooks/useAppNotifications";
import { useInHouseNotifications } from "@/hooks/useInHouseNotifications";
import {
  useIsWhitelabelDashboard,
  useIsWhitelabelOwner,
} from "@/hooks/useIsWhitelabelOwner";
import { useNotificationsSocket } from "@/hooks/useNotificationsSocket";
import { useTheme } from "@/hooks/useTheme";
import { useUser } from "@/hooks/useUser";
import { useWhitelabel } from "@/hooks/useWhitelabel";
import {
  formatActionLink,
  getNotificationTitle,
  NOTIFICATION_CONTENT_MAX_LINES,
  NotificationContentLines,
} from "@/utils/notificationDisplay";

import { LeaveImpersonate } from "./leave-impersonate";
import { ProjectsMenu } from "./projects-menu";
import { resolveBillingHref } from "./resolveBillingHref";
import { Search } from "./search";
import "./styles/inbox.css";
import { useHandleLogout } from "./useHandleLogout";

const INBOX_MAX_LINES = 5;

function NotificationInboxItem({
  config,
  onPress,
}: {
  config: CustomNotificationCard;
  onPress: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const text = config.notificationData.message.text;
  const lines = text.split("\n").filter((line) => line.trim() !== "");
  const hasMore = lines.length > INBOX_MAX_LINES;
  const collapsedLines = lines.slice(0, INBOX_MAX_LINES);
  const extraLines = lines.slice(INBOX_MAX_LINES);
  const avatar = config.notificationData.message.avatar?.avatar_url;

  return (
    <div className="px-4 py-2 flex flex-row justify-between hover:bg-bg-tertiary">
      <div className="flex items-start">
        {!avatar || avatar === "-" ? (
          <div className="w-8 h-8 rounded-full border-2 border-nav-item-button-icon-fg mr-3 flex items-center justify-center">
            <User2Icon className="text-nav-item-button-icon-fg" />
          </div>
        ) : (
          <img
            alt="avatar"
            className="w-8 h-8 rounded-full mr-3"
            src={avatar}
          />
        )}
        <div className="cursor-pointer" onClick={onPress}>
          <div className="flex items-center">
            <p
              className={`${config.notificationData.read_on === config.notificationData.seen_on ? "text-text-tertiary" : "text-fg-brand-primary-alt"} text-base font-medium`}
            >
              {config.notificationData.message.header}
            </p>
            <p className="text-sm font-normal text-text-tertiary ml-3">
              {dayjs(config.notificationData.created_on).fromNow()}
            </p>
          </div>
          <ul className="list-disc list-inside text-text-tertiary font-normal leading-normal">
            <p dangerouslySetInnerHTML={{ __html: collapsedLines[0] ?? "" }} />
            {collapsedLines.slice(1).map((line, index) => (
              <li className="pl-4" key={index}>
                {line.replace(/^- /, "")}{" "}
              </li>
            ))}
            <AnimatePresence initial={false}>
              {expanded && (
                <motion.div
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  initial={{ height: 0, opacity: 0 }}
                  style={{ overflow: "hidden" }}
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                >
                  {extraLines.map((line, index) => (
                    <li className="pl-4" key={index}>
                      {line.replace(/^- /, "")}{" "}
                    </li>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </ul>
          {hasMore && (
            <button
              className="mt-1 flex items-center gap-1 text-xs text-fg-brand-primary-alt hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
            >
              <motion.span
                animate={{ rotate: expanded ? 180 : 0 }}
                className="flex items-center gap-1"
                transition={{ duration: 0.2 }}
              >
                <ChevronDown size={13} />
              </motion.span>
              {expanded ? "Show less" : "Show more"}
            </button>
          )}
        </div>
        <div className=""></div>
      </div>
    </div>
  );
}

const BELL_TABS = [
  { countKey: "all" as const, label: "All", tag: undefined },
  { countKey: "mentions" as const, label: "Mentions", tag: "mention" as const },
  {
    countKey: "request" as const,
    label: "Request updates",
    tag: "request" as const,
  },
] as const;

type BellTabId = (typeof BELL_TABS)[number]["countKey"];

function NotificationBellItem({
  notification,
  onPress,
}: {
  notification: AppNotificationItem;
  onPress: () => void;
}) {
  return (
    <div className="px-4 py-2 flex flex-row justify-between hover:bg-bg-tertiary">
      <div className="flex items-start">
        <div className="w-8 h-8 rounded-full border-2 border-nav-item-button-icon-fg mr-3 flex items-center justify-center shrink-0">
          <User2Icon className="text-nav-item-button-icon-fg" />
        </div>
        <div className="cursor-pointer" onClick={onPress}>
          <div className="flex items-center">
            <p
              className={`${notification.isRead ? "text-text-tertiary" : "text-fg-brand-primary-alt"} text-base font-medium`}
            >
              {getNotificationTitle(notification.category, notification.tag)}
            </p>
            <p className="text-sm font-normal text-text-tertiary ml-3">
              {dayjs(notification.createdAt).fromNow()}
            </p>
          </div>
          {notification.content && (
            <NotificationContentLines
              content={notification.content}
              maxLines={NOTIFICATION_CONTENT_MAX_LINES}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function InHouseNotificationBell() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<BellTabId>("all");
  const [isOpen, setIsOpen] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const activeBellTab =
    BELL_TABS.find((tab) => tab.countKey === activeTab) ?? BELL_TABS[0];

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useAppNotifications({
      tag: activeBellTab.tag,
    });
  const { data: unreadCounts } = useUnreadNotificationCounts();
  const { mutate: updateNotification } = useUpdateAppNotification();
  const { mutate: bulkUpdate } = useBulkUpdateAppNotifications();

  const totalUnreadCount = unreadCounts?.all ?? 0;
  const notifications = data?.pages.flatMap((page) => page.notifications) ?? [];

  useEffect(() => {
    const sentinel = sentinelRef.current;

    if (!isOpen || !sentinel || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          fetchNextPage();
        }
      },
      { root: scrollContainerRef.current, rootMargin: "0px 0px 200px 0px" },
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [isOpen, fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleNotificationPress = (notification: AppNotificationItem) => {
    if (!notification.isRead) {
      updateNotification({ body: { isRead: true }, id: notification.id });
    }

    if (notification.url) {
      window.open(formatActionLink(notification.url), "_blank");
    }
  };

  return (
    <DialogTrigger onOpenChange={setIsOpen}>
      <MenuButton
        aria-label="Notifications"
        className="relative text-nav-item-button-icon-fg"
        isIconOnly
        noIndicator
        size="sm"
        variant="tertiary"
      >
        <Bell className="size-5" />
        {totalUnreadCount > 0 && (
          <span className="absolute right-1 top-1 flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-[var(--color-text-brand-secondary)] px-[3px] text-[10px] font-semibold leading-none text-white">
            {totalUnreadCount > 99 ? "99+" : totalUnreadCount}
          </span>
        )}
      </MenuButton>
      <Popover className="w-[420px] bg-bg-primary" offset={22}>
        <Dialog className="flex flex-col">
          {({ close }) => (
            <>
              <div className="px-4 pt-3">
                <div className="flex items-center justify-between">
                  <p className="text-base font-semibold text-text-primary">
                    Notifications
                  </p>
                  <button
                    className="text-xs font-medium text-fg-brand-primary-alt hover:underline"
                    onClick={() => bulkUpdate({ action: "markAllRead" })}
                  >
                    Mark all as read
                  </button>
                </div>
                <div className="mt-2 flex overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {BELL_TABS.map((tab) => {
                    const unreadCount = unreadCounts?.[tab.countKey] ?? 0;

                    return (
                      <button
                        className={`flex cursor-pointer items-center gap-[5px] px-3 pb-[6px] border-b-2 ${
                          activeTab === tab.countKey
                            ? "border-fg-brand-primary-alt"
                            : "border-transparent"
                        }`}
                        key={tab.countKey}
                        onClick={() => setActiveTab(tab.countKey)}
                      >
                        <span
                          className={`text-sm font-semibold whitespace-nowrap ${
                            activeTab === tab.countKey
                              ? "text-fg-brand-primary-alt"
                              : "text-text-quaternary"
                          }`}
                        >
                          {tab.label}
                        </span>
                        {unreadCount > 0 && (
                          <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[var(--color-text-brand-secondary)] px-[3px]">
                            <span className="text-[10px] font-semibold leading-none text-white">
                              {unreadCount > 99 ? "99+" : unreadCount}
                            </span>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div
                className="h-[400px] overflow-y-auto"
                ref={scrollContainerRef}
              >
                {notifications.length === 0 && (
                  <div className="px-4 py-6 text-center text-sm text-text-tertiary">
                    No notifications yet
                  </div>
                )}
                {notifications.map((notification) => (
                  <NotificationBellItem
                    key={notification.id}
                    notification={notification}
                    onPress={() => {
                      handleNotificationPress(notification);
                      close();
                    }}
                  />
                ))}
                {isFetchingNextPage && (
                  <p className="py-3 text-center text-sm text-text-tertiary">
                    Loading...
                  </p>
                )}
                <div ref={sentinelRef} />
              </div>
              <MenuSeparator />
              <button
                className="px-4 py-2 text-start text-sm font-medium text-text-secondary hover:bg-bg-active"
                onClick={() => {
                  close();
                  navigate({ to: "/notifications" as string });
                }}
              >
                View all notifications
              </button>
            </>
          )}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}

export function Navbar() {
  const { isDark, toggleTheme } = useTheme();
  const { setIsHovered, state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { config, isWhitelabel } = useWhitelabel();
  const [request, setRequest] = useState<string | undefined>();

  const { currentProject, data: user } = useUser();
  const useInHouse = useInHouseNotifications();

  useNotificationsSocket(useInHouse);
  const handleLogout = useHandleLogout();
  const isWhitelabelOwner = useIsWhitelabelOwner();
  // The stripped chrome (toggle/search/inbox) applies on the dashboard only.
  const isWhitelabelDashboard = useIsWhitelabelDashboard();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // The owner is "inside an account" whenever they are not on their dashboard.
  const isInsideAccount =
    isWhitelabelOwner && !pathname.startsWith("/whitelabel/dashboard");

  const isKickstarter = user?.user.accountType === "kickstarter";
  const isOwner = user?.user.roleId === UserRoleEnum.Owner;

  const billingHref = resolveBillingHref({
    email: user?.user.email,
    isKickstarter,
    isSubscriptionActive: currentProject?.subscriptionStatus.subscriptionActive,
  });

  const markRequestNotificationsAsRead = (config: CustomNotificationCard) => {
    const url = new URL(request ?? env.VITE_BASE_URL);
    const requestUrl = config.notificationData?.message.url;

    if (request && requestUrl?.includes(url.pathname)) {
      config.markAsRead();
    }
  };

  return (
    <header className="sticky top-0 z-40 h-navbar flex items-center justify-between border-b-2 border-border-secondary bg-bg-primary">
      <div className="flex py-xs justify-center items-center gap-xs">
        {!isWhitelabelDashboard && (
          <MenuButton
            aria-label="Toggle sidebar"
            className="text-nav-item-button-icon-fg"
            isIconOnly
            noIndicator
            onHoverChange={(hovered) => isCollapsed && setIsHovered(hovered)}
            onPress={toggleSidebar}
            size="sm"
            variant="tertiary"
          >
            <PanelLeftIcon />
          </MenuButton>
        )}
        {isWhitelabel ? (
          config?.logoUrl ? (
            <img alt="logo-image" className="w-[90px]" src={config.logoUrl} />
          ) : (
            ""
          )
        ) : (
          <Link
            aria-label="Go to request page"
            search={{ filter: "all" }}
            to="/requests"
          >
            <FullLogo className="w-auto h-[18px] items-center" />
          </Link>
        )}
        {isInsideAccount && (
          <Button
            aria-label="Manage account"
            className="gap-xs py-1 px-3"
            onPress={() => navigate({ to: "/whitelabel/dashboard" as string })}
            variant="secondary"
          >
            <GripIcon />
            <span className="hidden md:inline">Manage account</span>
          </Button>
        )}
      </div>

      <div className="flex py-xs pr-xs justify-center items-center gap-[2px] md:gap-xl">
        {!isWhitelabelDashboard && <Search />}
        <LeaveImpersonate />
        {!isWhitelabelDashboard && useInHouse && (
          <div className="z-999 w-[40px] relative flex justify-center items-center">
            <InHouseNotificationBell />
          </div>
        )}
        {!isWhitelabelDashboard && !useInHouse && (
          <div className="z-999 w-[40px] relative flex justify-center items-center">
            <Inbox
              notificationComponent={(config: CustomNotificationCard) => {
                markRequestNotificationsAsRead(config);

                return (
                  <NotificationInboxItem
                    config={config}
                    onPress={() => {
                      config.markAsRead();
                      setRequest(config?.notificationData?.message?.url);
                    }}
                  />
                );
              }}
              stores={[
                { label: "All", storeId: "All" },
                {
                  label: "Mentions",
                  query: { tags: "mentions" },
                  storeId: "mentions",
                },
                {
                  label: "Request updates",
                  query: { tags: "request" },
                  storeId: "request",
                },
              ]}
              tenantId={isWhitelabel && config ? config.tenantId! : "default"}
              theme={{
                notificationsContainer: {
                  loader: { color: "var(--color-text-brand-secondary)" },
                },
                tabs: {
                  badgeColor: "var(--color-text-brand-secondary)",
                  badgeText: "var(--color-text-white)",
                  bottomColor: "var(--color-text-brand-secondary)",
                  color: "var(--color-text-brand-secondary)",
                  unselectedColor: "var(--color-text-quaternary)",
                },
              }}
            />
          </div>
        )}
        <MenuTrigger>
          <MenuButton className="p-[2px]" noIndicator variant="unstyled">
            <Avatar
              alt={user?.user.name ?? ""}
              className="h-8 w-8 rounded-full"
              src={user?.user.avatar ?? ""}
            />
          </MenuButton>
          <MenuPopover offset={22}>
            <Menu>
              <ProjectsMenu />

              <MenuItem href="/user/profile">
                <AccessibleIcon>
                  <User />
                </AccessibleIcon>
                <MenuItemLabel>Profile</MenuItemLabel>
              </MenuItem>
              {isOwner && (
                <MenuItem href={billingHref}>
                  <AccessibleIcon>
                    <CreditCard />
                  </AccessibleIcon>
                  <MenuItemLabel>Billing</MenuItemLabel>
                </MenuItem>
              )}
              <MenuItem href="/notifications/settings">
                <AccessibleIcon>
                  <Settings />
                </AccessibleIcon>
                <MenuItemLabel>Notification Settings</MenuItemLabel>
              </MenuItem>
              <MenuSeparator />
              {(!isWhitelabel || isOwner) && (
                <MenuItem href="/make-a-suggestion">
                  <AccessibleIcon>
                    <ClipboardCheck />
                  </AccessibleIcon>
                  <MenuItemLabel>Make a suggestion</MenuItemLabel>
                </MenuItem>
              )}
              <MenuSeparator />
              <MenuItem
                // @ts-expect-error closeOnSelect is not supported according to the docs and is not in the types but it works somehow :D
                closeOnSelect={false}
                onAction={toggleTheme}
              >
                <div className="flex flex-row items-center gap-md">
                  <Switch className="pointer-events-none" isSelected={isDark}>
                    Dark mode
                  </Switch>
                  <span className="w-min rounded-full border border-utility-success-200 bg-utility-success-50 px-md py-xxs text-xs text-utility-success-700">
                    beta
                  </span>
                </div>
              </MenuItem>
              <MenuSeparator />
              <MenuItem onAction={handleLogout}>
                <AccessibleIcon>
                  <LogOut />
                </AccessibleIcon>
                <MenuItemLabel>Log out</MenuItemLabel>
              </MenuItem>
            </Menu>
          </MenuPopover>
        </MenuTrigger>
      </div>
    </header>
  );
}
